import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowLeft, Mail, Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { goToLogin } from "@/lib/auth";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Terjadi kesalahan. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const routeForRole = (role) => (!role ? "/onboarding" : role === "manager" ? "/admin" : role === "ecommerce_admin" ? "/ecommerce" : role === "affiliator" ? "/affiliator" : "/creator");

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate(routeForRole(user.role), { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !email.trim().toLowerCase().endsWith("@gmail.com")) {
      setError("Wajib menggunakan akun Gmail asli (@gmail.com)");
      return;
    }
    setSubmitting(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { name, email, password };
      const res = await api.post(path, body);
      const u = res.data.user;
      setUser(u);
      navigate(routeForRole(u.role), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-showty-cream" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src="/images/brand-products.webp" alt="Showtynion beauty" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-showty-navy/70 via-showty-navy/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-display text-5xl leading-tight">Where beauty meets community</h2>
          <p className="mt-3 text-white/70 max-w-sm">Unite in beauty, empower through confidence. Bergabung dengan komunitas beauty creators & affiliator Showtynion.</p>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center px-8 py-16 relative">
        <button onClick={() => navigate("/")} className="absolute top-8 left-8 flex items-center gap-2 text-sm text-muted-foreground hover:text-showty-red transition-colors" data-testid="back-home-btn">
          <ArrowLeft className="w-4 h-4" /> Beranda
        </button>
        <div className="w-full max-w-sm">
          <div className="text-center">
            <img src="/images/logo.png" alt="Showtynion" className="w-14 h-14 rounded-full object-cover mx-auto" />
            <h1 className="mt-6 font-display text-4xl text-showty-navy">{mode === "login" ? "Masuk ke Showtynion" : "Daftar Akun"}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{mode === "login" ? "Masuk dengan email & password atau Google." : "Buat akun baru — wajib pakai email Gmail asli (@gmail.com)."}</p>
          </div>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Nama</Label>
                <div className="relative mt-1.5">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="name" className="pl-9" placeholder="Nama kamu" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="email" type="email" className="pl-9" placeholder="kamu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" type="password" className="pl-9" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
              </div>
            </div>

            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

            <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="auth-submit">
              {submitting ? "Memproses…" : mode === "login" ? "Masuk" : "Daftar"}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            {mode === "login" ? "Belum punya akun?" : "Sudah punya akun?"}{" "}
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-showty-red font-medium underline underline-offset-2" data-testid="toggle-mode">
              {mode === "login" ? "Daftar" : "Masuk"}
            </button>
          </p>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> atau <div className="h-px flex-1 bg-border" />
          </div>

          <Button onClick={goToLogin} variant="outline" className="w-full h-11 rounded-full bg-white text-showty-navy hover:bg-showty-pink/40 flex items-center justify-center gap-3" data-testid="google-login-btn">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
            Lanjutkan dengan Google
          </Button>

          <p className="mt-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-showty-gold" /> Pilih peran kamu setelah masuk
          </p>
        </div>
      </div>
    </div>
  );
}
